// BRIEF 86 PHASE 2, STEP 10 (run by brief 90 §5) — the Description card, once.
//
// 72 lines in `CaseView` and 75 in `CollectiveCaseView`, 94% normalised — the
// highest-scoring pair in the brief's whole table, and its three runs were a
// comment, one free-name rename, and one real difference (the "↓ / ↑" direction
// on the show-more link, which CCV gained in the commit before this).
//
// NOTHING IS PARAMETERISED. Not the container discriminator, not a host handle:
// both sides read `cs.desc`, collapse on `sCol("desc")`, toggle with
// `tog("desc")` and clamp at the same 120 characters and three lines. `showDesc`
// is the host's own state under two names — `sShowDesc` on one side,
// `setShowDesc` on the other (docs/tier2-reading.md u3 named that pair) — and
// the prop takes the setter, whichever name the host gave it.
import { ChevronDown, ChevronRight } from "lucide-react";
import { FS } from "../lib/tokens";
import type { TFn } from "../lib/taxonomyTypes";

type Props = {
  cs: { desc?: string | null; [k: string]: unknown };
  showDesc: boolean;
  setShowDesc: (fn: (v: boolean) => boolean) => void;
  sCol: (k: string) => boolean;
  tog: (k: string) => void;
  t: TFn;
};

export default function CaseDescriptionCard({
  cs,
  showDesc,
  setShowDesc,
  sCol,
  tog,
  t,
}: Props) {
  return (
    <div className="detail-card" data-secollapsed={sCol("desc") ? "1" : "0"}>
      {(() => {
        const hasMore = !!(cs.desc && cs.desc.length > 120);
        return (
          <>
            <h4
              className="csSecHdr"
              style={{
                display: "flex",
                alignItems: "center",
                gap: 5,
                cursor: "pointer",
              }}
              onClick={() => tog("desc")}
            >
              {!sCol("desc") ? (
                <ChevronDown size={11} style={{ color: "var(--tm)" }} />
              ) : (
                <ChevronRight size={11} style={{ color: "var(--tm)" }} />
              )}
              {/* 58 §3 — one key per concept: `cc.desc` and `case.desc` were
                  byte-identical in all three languages. */}
              {t("case.desc")}
            </h4>
            <p
              style={{
                fontSize: FS.s11_5,
                color: "var(--ts)",
                lineHeight: 1.5,
                margin: 0,
                ...(!showDesc && cs.desc && hasMore
                  ? {
                      overflow: "hidden",
                      display: "-webkit-box",
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: "vertical",
                    }
                  : {}),
              }}
            >
              {cs.desc || (
                <span style={{ color: "var(--tm)", fontStyle: "italic" }}>
                  {t("case.no_description")}
                </span>
              )}
            </p>
            {hasMore && (
              <span
                className="link"
                style={{
                  fontSize: FS.s10,
                  cursor: "pointer",
                  display: "inline-block",
                  marginTop: 4,
                }}
                onClick={() => setShowDesc((v: boolean) => !v)}
              >
                {showDesc
                  ? "↑ " + t("common.show_less", { fallback: "Show less" })
                  : "↓ " + t("common.show_more", { fallback: "Show more" })}
              </span>
            )}
          </>
        );
      })()}
    </div>
  );
}
